// src/pages/ImportProspects.jsx
// Bulk Prospect Import — CSV/Excel upload wizard (4 steps).
// Completely separate from the client import system.
// Prospects go ONLY into the prospects/prospecting pipeline.

import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Upload, FileText, ChevronRight, Check, X, AlertTriangle,
  RefreshCw, ArrowLeft, Download, Info, CheckCircle2,
  SkipForward, RotateCcw, Users
} from 'lucide-react';

const BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

// ---------------------------------------------------------------------------
// Field definitions
// ---------------------------------------------------------------------------
const PROSPECT_FIELDS = [
  { key: 'first_name',      label: 'First Name',       required: true  },
  { key: 'last_name',       label: 'Last Name',        required: true  },
  { key: 'phone',           label: 'Phone',            required: false },
  { key: 'email',           label: 'Email',            required: false },
  { key: 'dob',             label: 'Date of Birth',    required: false },
  { key: 'address',         label: 'Address',          required: false },
  { key: 'city',            label: 'City',             required: false },
  { key: 'state',           label: 'State',            required: false },
  { key: 'zip',             label: 'ZIP Code',         required: false },
  { key: 'pipeline_stage',  label: 'Pipeline Stage',   required: false },
  { key: 'lead_source',     label: 'Lead Source',      required: false },
  { key: 'tcpa_consent',    label: 'TCPA Consent',     required: false },
  { key: 'current_carrier', label: 'Current Carrier',  required: false },
  { key: 'insurance_type',  label: 'Insurance Type',   required: false },
  { key: 'notes',           label: 'Notes',            required: false },
];

// Auto-mapping: header variations → field key
const HEADER_MAP = {
  first_name:      ['first name','firstname','fname','first','given name','givenname'],
  last_name:       ['last name','lastname','lname','last','surname','family name'],
  phone:           ['phone','cell','mobile','telephone','cell phone','phone number','ph','tel'],
  email:           ['email','email address','e-mail','mail'],
  dob:             ['dob','date of birth','birthdate','birthday','birth date','birth'],
  address:         ['address','street','street address','addr','street addr'],
  city:            ['city','town','municipality'],
  state:           ['state','st','province','region'],
  zip:             ['zip','zip code','postal','postal code','zipcode'],
  pipeline_stage:  ['stage','pipeline stage','pipeline','status'],
  lead_source:     ['source','lead source','how did you hear','referral source','origin'],
  tcpa_consent:    ['tcpa','consent','text consent','sms consent','opt in','opt-in','optin','text opt in','texting consent'],
  current_carrier: ['carrier','current carrier','insurance carrier','insurance company','insurer'],
  insurance_type:  ['insurance type','plan type','type','product','coverage type'],
  notes:           ['notes','note','comments','comment','remarks'],
};

function guessField(header) {
  const h = (header || '').toLowerCase().trim().replace(/[_\-]/g, ' ');
  for (const [field, variants] of Object.entries(HEADER_MAP)) {
    if (variants.includes(h)) return field;
  }
  return '';
}

// Detect header row (not a data row)
function looksLikeHeader(row) {
  const nonEmpty = row.filter(c => String(c || '').trim().length > 0);
  if (nonEmpty.length < 2) return false;
  const allShort    = nonEmpty.every(c => String(c).length < 60);
  const noNumbers   = nonEmpty.filter(c => /^\d+$/.test(String(c).trim())).length === 0;
  return allShort && noNumbers;
}

// Parse CSV manually (handles quoted commas)
function parseCSV(text) {
  const lines = [];
  let cur = '', inQ = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '"') { inQ = !inQ; }
    else if (ch === '\n' && !inQ) { lines.push(cur); cur = ''; }
    else { cur += ch; }
  }
  if (cur) lines.push(cur);

  return lines.map(line => {
    const cells = []; let cell = '', q = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') { q = !q; }
      else if (c === ',' && !q) { cells.push(cell.trim()); cell = ''; }
      else { cell += c; }
    }
    cells.push(cell.trim());
    return cells;
  }).filter(r => r.some(c => c.trim()));
}

// ---------------------------------------------------------------------------
// Sample CSV download
// ---------------------------------------------------------------------------
const SAMPLE_CSV = `first_name,last_name,phone,email,dob,address,city,state,zip,pipeline_stage,lead_source,tcpa_consent,notes
John,Smith,5551234567,john@example.com,1958-03-15,123 Main St,Columbus,OH,43215,lead,referral,true,Turning 65 in March
Jane,Doe,5559876543,,1959-07-22,,,,, qualified,facebook_ad,false,
`;

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function ImportProspects() {
  const navigate = useNavigate();
  const fileRef  = useRef(null);

  const [step, setStep]             = useState(1); // 1=upload 2=map 3=preview 4=done
  const [dragging, setDragging]     = useState(false);
  const [rawRows, setRawRows]       = useState([]);   // all parsed rows
  const [headers, setHeaders]       = useState([]);   // detected CSV headers
  const [mapping, setMapping]       = useState({});   // { csvHeader: fieldKey }
  const [filename, setFilename]     = useState('');
  const [previewRows, setPreviewRows] = useState([]); // first 5 rows
  const [duplicates, setDuplicates] = useState([]);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [importing, setImporting]   = useState(false);
  const [result, setResult]         = useState(null);
  const [imports, setImports]       = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [err, setErr]               = useState('');

  // ---------------------------------------------------------------------------
  // Step 1 — File handling
  // ---------------------------------------------------------------------------
  function handleFile(file) {
    if (!file) return;
    setErr('');
    setFilename(file.name);
    const ext = file.name.split('.').pop().toLowerCase();

    if (ext === 'csv') {
      const reader = new FileReader();
      reader.onload = e => processRaw(parseCSV(e.target.result));
      reader.readAsText(file);
    } else if (ext === 'xlsx' || ext === 'xls') {
      // Lazy-load XLSX
      import('xlsx').then(XLSX => {
        const reader = new FileReader();
        reader.onload = e => {
          const wb  = XLSX.read(new Uint8Array(e.target.result), { type: 'array' });
          const ws  = wb.Sheets[wb.SheetNames[0]];
          const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
          processRaw(raw.map(r => r.map(c => String(c ?? ''))));
        };
        reader.readAsArrayBuffer(file);
      }).catch(() => setErr('XLSX library not available. Please use CSV format.'));
    } else {
      setErr('Please upload a .csv, .xlsx, or .xls file');
    }
  }

  function processRaw(rows) {
    if (!rows.length) { setErr('File appears to be empty'); return; }

    // Find header row (usually row 0, but skip title rows)
    let headerIdx = 0;
    for (let i = 0; i < Math.min(5, rows.length); i++) {
      if (looksLikeHeader(rows[i])) { headerIdx = i; break; }
    }

    const hdrs = rows[headerIdx].map(h => String(h || '').trim());
    const data  = rows.slice(headerIdx + 1).filter(r => r.some(c => String(c || '').trim()));

    // Auto-map
    const autoMap = {};
    hdrs.forEach(h => {
      const guess = guessField(h);
      if (guess) autoMap[h] = guess;
    });

    setHeaders(hdrs);
    setRawRows(data);
    setMapping(autoMap);
    setPreviewRows(data.slice(0, 5));
    setStep(2);
  }

  const onDrop = useCallback(e => {
    e.preventDefault(); setDragging(false);
    handleFile(e.dataTransfer.files[0]);
  }, []);

  // ---------------------------------------------------------------------------
  // Step 2→3 — Build items from mapping
  // ---------------------------------------------------------------------------
  function buildItems(rows) {
    return rows.map(row => {
      const item = {};
      headers.forEach((h, i) => {
        const field = mapping[h];
        if (field) item[field] = String(row[i] ?? '').trim();
      });
      return item;
    });
  }

  async function handleReview() {
    setErr('');
    const items = buildItems(rawRows);

    // Check required
    const missing = items.filter(it => !it.first_name || !it.last_name);
    if (missing.length === items.length) {
      setErr('No rows have both First Name and Last Name mapped. Please check your column mapping.');
      return;
    }

    // Pre-check duplicates
    try {
      const r = await fetch(`${BASE}/api/prospecting/check-duplicates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ items }),
      });
      const j = await r.json();
      setDuplicates(j.duplicates || []);
    } catch { setDuplicates([]); }

    setStep(3);
  }

  // ---------------------------------------------------------------------------
  // Step 3→4 — Import
  // ---------------------------------------------------------------------------
  async function handleImport() {
    setImporting(true);
    setErr('');
    try {
      const items = buildItems(rawRows);
      const r = await fetch(`${BASE}/api/prospecting/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ items, filename, skipDuplicates }),
      });
      const j = await r.json();
      if (j.ok) {
        setResult(j);
        setStep(4);
        loadHistory();
      } else {
        setErr(j.error || 'Import failed');
      }
    } catch { setErr('Network error — please try again'); }
    finally { setImporting(false); }
  }

  // ---------------------------------------------------------------------------
  // History & reverse
  // ---------------------------------------------------------------------------
  async function loadHistory() {
    setLoadingHistory(true);
    try {
      const r = await fetch(`${BASE}/api/prospecting/imports`, { credentials: 'include' });
      const j = await r.json();
      if (j.ok) setImports(j.data || []);
    } catch {}
    setLoadingHistory(false);
  }

  async function reverseImport(id) {
    if (!window.confirm('This will permanently delete all prospects from this import batch. Continue?')) return;
    try {
      const r = await fetch(`${BASE}/api/prospecting/imports/${id}/reverse`, {
        method: 'POST', credentials: 'include',
      });
      const j = await r.json();
      if (j.ok) loadHistory();
    } catch {}
  }

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------
  const mappedCount  = Object.values(mapping).filter(Boolean).length;
  const totalRows    = rawRows.length;
  const dupCount     = duplicates.length;
  const requiredMapped = mapping[headers.find(h => guessField(h) === 'first_name') || ''] === 'first_name' ||
    Object.values(mapping).includes('first_name');

  function downloadSample() {
    const blob = new Blob([SAMPLE_CSV], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'prospect-import-template.csv';
    a.click();
  }

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Header */}
      <div className="border-b border-gray-800 bg-gray-900 px-6 py-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/prospecting')}
            className="text-gray-400 hover:text-white transition flex items-center gap-1 text-sm"
          >
            <ArrowLeft size={16} /> Back to Pipeline
          </button>
          <div className="w-px h-5 bg-gray-700" />
          <div className="flex items-center gap-2">
            <Upload size={18} className="text-amber-400" />
            <h1 className="text-lg font-bold">Import Prospects</h1>
          </div>
        </div>

        {/* Step progress */}
        <div className="flex items-center gap-2 mt-4">
          {['Upload', 'Map Columns', 'Review', 'Done'].map((label, i) => (
            <React.Fragment key={i}>
              <div className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition ${
                  step > i + 1 ? 'bg-emerald-500 text-white' :
                  step === i + 1 ? 'bg-amber-400 text-gray-900' :
                  'bg-gray-700 text-gray-500'
                }`}>
                  {step > i + 1 ? <Check size={12} /> : i + 1}
                </div>
                <span className={`text-xs font-medium ${step === i + 1 ? 'text-white' : 'text-gray-500'}`}>{label}</span>
              </div>
              {i < 3 && <div className={`flex-1 h-px max-w-[60px] ${step > i + 1 ? 'bg-emerald-500' : 'bg-gray-700'}`} />}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6">
        {/* STEP 1: Upload */}
        {step === 1 && (
          <div>
            {/* Info banner */}
            <div className="flex items-start gap-3 bg-blue-500/10 border border-blue-500/20 rounded-xl px-4 py-3 mb-6">
              <Info size={16} className="text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-blue-300 text-sm font-semibold">Prospects only — completely separate from Clients</p>
                <p className="text-blue-200/70 text-xs mt-0.5">
                  Imported records go into your Prospecting Pipeline. They are not visible in the Clients section.
                  You can convert individual prospects to clients manually later.
                </p>
              </div>
            </div>

            {/* Drop zone */}
            <div
              onDragOver={e => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              onClick={() => fileRef.current?.click()}
              className={`border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition ${
                dragging ? 'border-amber-400 bg-amber-400/5' : 'border-gray-700 hover:border-gray-500 bg-gray-900/50'
              }`}
            >
              <Upload size={40} className="text-gray-600 mx-auto mb-4" />
              <p className="text-white font-semibold mb-1">Drop your file here, or click to browse</p>
              <p className="text-gray-500 text-sm">Supports CSV, Excel (.xlsx, .xls)</p>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                className="hidden"
                onChange={e => handleFile(e.target.files[0])}
              />
            </div>

            {err && <p className="text-red-400 text-sm mt-3">{err}</p>}

            {/* Sample + required fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-white text-sm font-semibold flex items-center gap-2">
                    <FileText size={14} className="text-amber-400" /> Required Columns
                  </h3>
                  <button
                    onClick={downloadSample}
                    className="text-amber-400 text-xs hover:text-amber-300 flex items-center gap-1 transition"
                  >
                    <Download size={12} /> Download Template
                  </button>
                </div>
                <div className="space-y-1.5">
                  {PROSPECT_FIELDS.map(f => (
                    <div key={f.key} className="flex items-center gap-2 text-xs">
                      {f.required
                        ? <span className="text-amber-400 font-bold">*</span>
                        : <span className="text-gray-700">·</span>
                      }
                      <span className={f.required ? 'text-white font-medium' : 'text-gray-400'}>{f.label}</span>
                      <span className="text-gray-700 font-mono">{f.key}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                <h3 className="text-white text-sm font-semibold mb-3">Valid Values</h3>
                <div className="space-y-2 text-xs">
                  <div>
                    <p className="text-gray-400 font-medium mb-1">pipeline_stage</p>
                    <p className="text-gray-600 font-mono">lead · qualified · appointment_set · follow_up · closed_won · closed_lost</p>
                  </div>
                  <div>
                    <p className="text-gray-400 font-medium mb-1">lead_source</p>
                    <p className="text-gray-600 font-mono">referral · facebook_ad · google_ad · direct_mail · web_form · purchased_list · t65_aged · cold_call · social_media · other</p>
                  </div>
                  <div>
                    <p className="text-gray-400 font-medium mb-1">tcpa_consent</p>
                    <p className="text-gray-600 font-mono">true / false / yes / no / 1 / 0</p>
                  </div>
                  <div>
                    <p className="text-gray-400 font-medium mb-1">dob format</p>
                    <p className="text-gray-600 font-mono">YYYY-MM-DD or MM/DD/YYYY</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Import history */}
            {imports.length > 0 && (
              <div className="mt-6 bg-gray-900 border border-gray-800 rounded-xl p-5">
                <h3 className="text-white text-sm font-semibold mb-3 flex items-center gap-2">
                  <RefreshCw size={14} className="text-gray-400" /> Recent Imports
                </h3>
                <div className="flex flex-col gap-2">
                  {imports.map(imp => (
                    <div key={imp.id} className="flex items-center justify-between text-xs py-2 border-b border-gray-800 last:border-0">
                      <div>
                        <span className="text-white font-medium">{imp.filename}</span>
                        <span className="text-gray-500 ml-3">{new Date(imp.created_at).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-emerald-400">+{imp.created_count} added</span>
                        <span className="text-gray-500">{imp.skipped_count} skipped</span>
                        {imp.status === 'reversed' ? (
                          <span className="text-gray-600 italic">Reversed</span>
                        ) : (
                          <button
                            onClick={() => reverseImport(imp.id)}
                            className="text-red-400 hover:text-red-300 flex items-center gap-1 transition"
                          >
                            <RotateCcw size={11} /> Undo
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* STEP 2: Map Columns */}
        {step === 2 && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-white font-semibold">Map Your Columns</h2>
                <p className="text-gray-500 text-xs mt-0.5">
                  {filename} — {totalRows} rows detected &nbsp;·&nbsp; {mappedCount} columns mapped
                </p>
              </div>
              <button onClick={() => setStep(1)} className="text-gray-400 hover:text-white text-sm flex items-center gap-1 transition">
                <ArrowLeft size={14} /> Re-upload
              </button>
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden mb-4">
              <div className="grid grid-cols-2 gap-0 text-xs font-semibold text-gray-400 bg-gray-800 px-5 py-2.5">
                <span>CSV Column</span>
                <span>Maps to Prospect Field</span>
              </div>
              {headers.map(h => (
                <div key={h} className="grid grid-cols-2 items-center gap-4 px-5 py-3 border-t border-gray-800">
                  <div>
                    <p className="text-white text-sm font-medium">{h}</p>
                    <p className="text-gray-600 text-xs mt-0.5 truncate">
                      e.g. {String(rawRows[0]?.[headers.indexOf(h)] ?? '').slice(0, 40) || '—'}
                    </p>
                  </div>
                  <select
                    value={mapping[h] || ''}
                    onChange={e => setMapping(m => ({ ...m, [h]: e.target.value }))}
                    className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-amber-400"
                  >
                    <option value="">— Skip this column —</option>
                    {PROSPECT_FIELDS.map(f => (
                      <option key={f.key} value={f.key}>
                        {f.label}{f.required ? ' *' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>

            {err && <p className="text-red-400 text-sm mb-3">{err}</p>}

            <div className="flex justify-between">
              <button onClick={() => setStep(1)} className="text-gray-400 hover:text-white text-sm transition">← Back</button>
              <button
                onClick={handleReview}
                className="bg-amber-400 text-gray-900 font-semibold px-6 py-2 rounded-lg hover:bg-amber-300 transition text-sm"
              >
                Review Data →
              </button>
            </div>
          </div>
        )}

        {/* STEP 3: Review */}
        {step === 3 && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-white font-semibold">Review & Import</h2>
                <p className="text-gray-500 text-xs mt-0.5">{totalRows} prospects ready to import</p>
              </div>
            </div>

            {/* Duplicate warning */}
            {dupCount > 0 && (
              <div className="flex items-start gap-3 bg-amber-400/10 border border-amber-400/30 rounded-xl px-4 py-3 mb-4">
                <AlertTriangle size={16} className="text-amber-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-amber-300 text-sm font-semibold">{dupCount} potential duplicate{dupCount > 1 ? 's' : ''} found</p>
                  <p className="text-amber-200/70 text-xs mt-0.5">These prospects appear to already exist in your pipeline (matched by phone or name).</p>
                  <div className="flex items-center gap-3 mt-2">
                    <button
                      onClick={() => setSkipDuplicates(true)}
                      className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition ${skipDuplicates ? 'border-amber-400 text-amber-400 bg-amber-400/10' : 'border-gray-600 text-gray-400'}`}
                    >
                      <SkipForward size={12} /> Skip duplicates ({dupCount})
                    </button>
                    <button
                      onClick={() => setSkipDuplicates(false)}
                      className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition ${!skipDuplicates ? 'border-amber-400 text-amber-400 bg-amber-400/10' : 'border-gray-600 text-gray-400'}`}
                    >
                      Import anyway
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Preview table */}
            <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-x-auto mb-4">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-800 bg-gray-800">
                    <th className="px-4 py-2.5 text-left text-gray-400 font-medium">#</th>
                    {PROSPECT_FIELDS.filter(f => Object.values(mapping).includes(f.key)).map(f => (
                      <th key={f.key} className="px-4 py-2.5 text-left text-gray-400 font-medium">{f.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {buildItems(previewRows).map((row, i) => (
                    <tr key={i} className="border-b border-gray-800/50 last:border-0">
                      <td className="px-4 py-2 text-gray-600">{i + 1}</td>
                      {PROSPECT_FIELDS.filter(f => Object.values(mapping).includes(f.key)).map(f => (
                        <td key={f.key} className={`px-4 py-2 ${f.required && !row[f.key] ? 'text-red-400' : 'text-gray-300'}`}>
                          {String(row[f.key] ?? '—').slice(0, 40)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {totalRows > 5 && (
                <p className="text-center text-gray-600 text-xs py-2 border-t border-gray-800">
                  Showing 5 of {totalRows} rows
                </p>
              )}
            </div>

            {/* Summary */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-white">{totalRows}</p>
                <p className="text-gray-500 text-xs mt-1">Total rows</p>
              </div>
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-amber-400">{dupCount}</p>
                <p className="text-gray-500 text-xs mt-1">Duplicates {skipDuplicates ? '(will skip)' : '(will import)'}</p>
              </div>
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-emerald-400">
                  {skipDuplicates ? Math.max(0, totalRows - dupCount) : totalRows}
                </p>
                <p className="text-gray-500 text-xs mt-1">Expected to import</p>
              </div>
            </div>

            {err && <p className="text-red-400 text-sm mb-3">{err}</p>}

            <div className="flex justify-between">
              <button onClick={() => setStep(2)} className="text-gray-400 hover:text-white text-sm transition">← Back</button>
              <button
                onClick={handleImport}
                disabled={importing}
                className="bg-amber-400 text-gray-900 font-semibold px-6 py-2 rounded-lg hover:bg-amber-300 transition text-sm disabled:opacity-50 flex items-center gap-2"
              >
                {importing
                  ? <><RefreshCw size={14} className="animate-spin" /> Importing…</>
                  : <><Upload size={14} /> Import {skipDuplicates ? totalRows - dupCount : totalRows} Prospects</>
                }
              </button>
            </div>
          </div>
        )}

        {/* STEP 4: Done */}
        {step === 4 && result && (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 size={36} className="text-emerald-400" />
            </div>
            <h2 className="text-white text-2xl font-bold mb-2">Import Complete</h2>
            <p className="text-gray-400 text-sm mb-8">Your prospects have been added to the pipeline.</p>

            <div className="grid grid-cols-3 gap-4 max-w-sm mx-auto mb-8">
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                <p className="text-3xl font-bold text-emerald-400">{result.created}</p>
                <p className="text-gray-500 text-xs mt-1">Added</p>
              </div>
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                <p className="text-3xl font-bold text-gray-400">{result.skipped}</p>
                <p className="text-gray-500 text-xs mt-1">Skipped</p>
              </div>
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                <p className="text-3xl font-bold text-red-400">{result.errors}</p>
                <p className="text-gray-500 text-xs mt-1">Errors</p>
              </div>
            </div>

            <div className="flex items-center justify-center gap-3">
              <button
                onClick={() => navigate('/prospecting')}
                className="bg-amber-400 text-gray-900 font-semibold px-6 py-2.5 rounded-lg hover:bg-amber-300 transition flex items-center gap-2"
              >
                <Users size={16} /> View in Pipeline
              </button>
              <button
                onClick={() => { setStep(1); setResult(null); setRawRows([]); setHeaders([]); setMapping({}); }}
                className="bg-gray-800 border border-gray-700 text-white px-6 py-2.5 rounded-lg hover:bg-gray-700 transition text-sm"
              >
                Import Another File
              </button>
            </div>

            {/* Undo this import */}
            {result.importId && (
              <div className="mt-8 text-center">
                <button
                  onClick={() => reverseImport(result.importId).then(() => navigate('/prospecting'))}
                  className="text-gray-600 hover:text-red-400 text-xs flex items-center gap-1 mx-auto transition"
                >
                  <RotateCcw size={11} /> Undo this import
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
